import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, AlertTriangle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  critico: "bg-destructive/5 border-l-2 border-destructive",
  interconsulta: "bg-blue-50 border-l-2 border-blue-400",
  default: "hover:bg-secondary/50",
};

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(resp.data.count || 0);
    } catch {}
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(resp.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  const markRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-secondary transition-all"
        data-testid="notification-bell"
        title="Alertas clínicos"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-border rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <span className="text-sm font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>
                Notificações
              </span>
              {unreadCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-destructive/10 text-destructive rounded-full font-medium">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-3 h-3" />
                Marcar lidos
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-border/50">
            {loading ? (
              <div className="p-4 space-y-2">
                {[1, 2].map(i => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Nenhum alerta clínico</p>
              </div>
            ) : (
              notifications.map(n => (
                <Link
                  key={n.notification_id}
                  to={n.type === "interconsulta" ? "/teleconsultation" : n.wound_id ? `/wounds/${n.wound_id}` : n.patient_id ? `/patients/${n.patient_id}` : "#"}
                  onClick={() => { if (!n.is_read) markRead(n.notification_id); setOpen(false); }}
                  className={`block px-4 py-3 transition-all ${!n.is_read ? TYPE_STYLES[n.type] || TYPE_STYLES.default : "hover:bg-secondary/30"}`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.is_read ? "bg-destructive" : "bg-border"}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold ${!n.is_read ? "text-foreground" : "text-muted-foreground"}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        {new Date(n.created_at).toLocaleString("pt-BR", {
                          day: "2-digit", month: "2-digit",
                          hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
